import './Picture.scss'

import React, {useState} from 'react'
import {ImageType} from '../../types'
import {style} from '../../util/style'

const styles = style('picture')

const WIDTHS = [100, 200, 400, 600, 800]
const HEIGHTS = [100, 200, 400, 600, 800]

function getResizeUrl(src: string, width: number, height: number) {
	const w = getResizedWidth(width)
	let h: number | 'auto' = HEIGHTS.find((calcH) => calcH >= height)
	if (!h) h = 'auto'

	return `/cache/${w}/${h}${src}`
}

function getResizedWidth(width: number): number {
	let w: number = WIDTHS.find((calcW) => calcW >= width)
	if (!w) w = WIDTHS[WIDTHS.length - 1]
	return w
}

function getMimeType(extension: string): string {
	switch (extension) {
		case 'apng':
			return 'image/apng'
		case 'bmp':
			return 'image/bmp'
		case 'gif':
			return 'image/gif'
		case 'ico':
		case 'cur':
			return 'image/x-ico'
		case 'jpg':
		case 'jpeg':
		case 'jfif':
		case 'pjpeg':
		case 'pjp':
			return 'image/jpeg'
		case 'png':
			return 'image/png'
		case 'svg':
			return 'image/svg+xml'
		case 'tif':
		case 'tiff':
			return 'image/tiff'
		case 'webp':
			return 'image/webp'
	}
	return extension
}

function getSource(
	w: number,
	src: string,
	addWebpExt: boolean,
	isLast: boolean,
	type: string
) {
	return (
		<>
			<source
				media={`(max-width: ${w}px)`}
				srcSet={`
						${getResizeUrl(src, w * 0.5, Infinity)}${addWebpExt ? '.webp' : ''} 1x,
						${getResizeUrl(src, w * 0.75, Infinity)}${addWebpExt ? '.webp' : ''} 1.5x,
						${getResizeUrl(src, w, Infinity)}${addWebpExt ? '.webp' : ''} 2x`}
				type={getMimeType(addWebpExt ? 'webp' : type)}
			/>
			{isLast && (
				<>
					<source
						media={`(max-width: ${w * 1.5}px)`}
						srcSet={`
									${getResizeUrl(src, w * 0.75, Infinity)}${addWebpExt ? '.webp' : ''}`}
						type={getMimeType(addWebpExt ? 'webp' : type)}
					/>
					<source
						media={`(min-width: ${w * 1.5 + 1}px)`}
						srcSet={`
									${getResizeUrl(src, w, Infinity)}${addWebpExt ? '.webp' : ''}`}
						type={getMimeType(addWebpExt ? 'webp' : type)}
					/>
				</>
			)}
		</>
	)
}

function getSources(src: string) {
	return WIDTHS.sort((a: number, b: number) => (a < b ? a : b)).map((w, i) => {
		const isLast: boolean = i === WIDTHS.length - 1
		const type: string = src.toLowerCase().split('.').slice(-1)[0]
		return (
			<>
				{type !== 'webp' && getSource(w, src, true, isLast, type)}
				{getSource(w, src, false, isLast, type)}
			</>
		)
	})
}

type PictureProps = ImageType & {className?: string}

export const Picture: React.FC<PictureProps> = ({
	src,
	width,
	height,
	meta,
	className,
	...rest
}) => {
	const [isLoaded, setLoaded] = useState(false)
	const resizedWidth: number = getResizedWidth(width) * 2
	const resizedHeight: number = (height / width) * resizedWidth
	const aspectRatio: number = resizedHeight / resizedWidth

	return (
		<picture className={styles().with(className)}>
			{getSources(src)}
			<img
				{...rest}
				src={getResizeUrl(src, WIDTHS[WIDTHS.length - 1], Infinity)}
				width={resizedWidth}
				height={resizedHeight}
				className={styles('image')}
				onLoad={() => setLoaded(true)}
				style={{
					opacity: isLoaded ? 1 : 0,
					transition: 'opacity 250ms ease-out'
				}}
			/>
		</picture>
	)
}
